import * as THREE from 'three';

const SNAP_COLOR = 0x00ff00; // AutoCAD varsayılan yeşil

export function createSnapMarker(size = 8, snapMarker) {
  const snapType = snapMarker?.type;
  const half = size / 2;
  let geometry, material, marker;

  switch (snapType) {
    case 'start':
    case 'end':
    case 'startpoint':
    case 'endpoint':
    case 'vertex': {
      // □ Endpoint / Vertex: Kare (AutoCAD endpoint marker)
      const points = [
        new THREE.Vector3(-half, -half, 0),
        new THREE.Vector3(half, -half, 0),
        new THREE.Vector3(half, half, 0),
        new THREE.Vector3(-half, half, 0),
        new THREE.Vector3(-half, -half, 0),
      ];
      geometry = new THREE.BufferGeometry().setFromPoints(points);
      material = new THREE.LineBasicMaterial({ color: SNAP_COLOR, depthTest: false });
      marker = new THREE.Line(geometry, material);
      break;
    }

    case 'midpoint': {
      // △ Midpoint: Üçgen (AutoCAD midpoint marker)
      const h = size * 0.866; // sqrt(3)/2
      const points = [
        new THREE.Vector3(0, h / 2, 0),
        new THREE.Vector3(-half, -h / 2, 0),
        new THREE.Vector3(half, -h / 2, 0),
        new THREE.Vector3(0, h / 2, 0),
      ];
      geometry = new THREE.BufferGeometry().setFromPoints(points);
      material = new THREE.LineBasicMaterial({ color: SNAP_COLOR, depthTest: false });
      marker = new THREE.Line(geometry, material);
      break;
    }

    case 'center': {
      // ○ Center: Daire çizgisi (AutoCAD center marker)
      const segments = 32;
      const circlePoints = [];
      for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        circlePoints.push(new THREE.Vector3(
          Math.cos(angle) * half,
          Math.sin(angle) * half,
          0
        ));
      }
      geometry = new THREE.BufferGeometry().setFromPoints(circlePoints);
      material = new THREE.LineBasicMaterial({ color: SNAP_COLOR, depthTest: false });
      marker = new THREE.Line(geometry, material);
      break;
    }

    case 'quadrant': {
      // ◇ Quadrant: Eşkenar dörtgen / Diamond (AutoCAD quadrant marker)
      const points = [
        new THREE.Vector3(0, half, 0),
        new THREE.Vector3(half, 0, 0),
        new THREE.Vector3(0, -half, 0),
        new THREE.Vector3(-half, 0, 0),
        new THREE.Vector3(0, half, 0),
      ];
      geometry = new THREE.BufferGeometry().setFromPoints(points);
      material = new THREE.LineBasicMaterial({ color: SNAP_COLOR, depthTest: false });
      marker = new THREE.Line(geometry, material);
      break;
    }

    case 'diagonal': {
      // ✕ Diagonal: Çarpı işareti
      const points = [
        new THREE.Vector3(-half, -half, 0),
        new THREE.Vector3(half, half, 0),
        new THREE.Vector3(-half, half, 0),
        new THREE.Vector3(half, -half, 0),
      ];
      geometry = new THREE.BufferGeometry().setFromPoints(points);
      material = new THREE.LineBasicMaterial({ color: SNAP_COLOR, depthTest: false });
      marker = new THREE.LineSegments(geometry, material);
      break;
    }

    case 'intersection': {
      // ✕ Intersection: Çarpı (AutoCAD intersection marker)
      const points = [
        new THREE.Vector3(-half, -half, 0),
        new THREE.Vector3(half, half, 0),
        new THREE.Vector3(-half, half, 0),
        new THREE.Vector3(half, -half, 0),
      ];
      geometry = new THREE.BufferGeometry().setFromPoints(points);
      material = new THREE.LineBasicMaterial({ color: SNAP_COLOR, depthTest: false });
      marker = new THREE.LineSegments(geometry, material);
      break;
    }

    case 'perpendicular': {
      // ⊥ Perpendicular (AutoCAD perpendicular marker)
      const points = [
        new THREE.Vector3(-half, -half, 0),
        new THREE.Vector3(half, -half, 0),
        new THREE.Vector3(half, -half, 0),
        new THREE.Vector3(half, half, 0),
      ];
      geometry = new THREE.BufferGeometry().setFromPoints(points);
      material = new THREE.LineBasicMaterial({ color: SNAP_COLOR, depthTest: false });
      marker = new THREE.LineSegments(geometry, material);
      break;
    }

    case 'tangent': {
      // ○─ Tangent (AutoCAD tangent marker): daire + yatay çizgi
      const segments = 32;
      const r = half * 0.7;
      const tangentPoints = [];
      for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        tangentPoints.push(new THREE.Vector3(Math.cos(angle) * r, Math.sin(angle) * r, 0));
      }
      // Yatay teğet çizgi
      tangentPoints.push(new THREE.Vector3(NaN, NaN, NaN));
      tangentPoints.push(new THREE.Vector3(-half, -r, 0));
      tangentPoints.push(new THREE.Vector3(half, -r, 0));

      const validPoints = tangentPoints.filter(p => !isNaN(p.x));
      geometry = new THREE.BufferGeometry().setFromPoints(validPoints);
      material = new THREE.LineBasicMaterial({ color: SNAP_COLOR, depthTest: false });
      marker = new THREE.Line(geometry, material);
      break;
    }

    case 'nearest': {
      // ⏳ Nearest: Kum saati (AutoCAD nearest marker)
      const points = [
        new THREE.Vector3(-half, half, 0),
        new THREE.Vector3(half, half, 0),
        new THREE.Vector3(-half, -half, 0),
        new THREE.Vector3(half, -half, 0),
        new THREE.Vector3(-half, half, 0),
      ];
      geometry = new THREE.BufferGeometry().setFromPoints(points);
      material = new THREE.LineBasicMaterial({ color: SNAP_COLOR, depthTest: false });
      marker = new THREE.Line(geometry, material);
      break;
    }

    case 'node':
    case 'insertion': {
      // ⊕ Node: Daire + çarpı (AutoCAD node marker)
      const segments = 32;
      const nodePoints = [];
      for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        nodePoints.push(new THREE.Vector3(Math.cos(angle) * half, Math.sin(angle) * half, 0));
      }
      geometry = new THREE.BufferGeometry().setFromPoints(nodePoints);
      material = new THREE.LineBasicMaterial({ color: SNAP_COLOR, depthTest: false });
      const circle = new THREE.Line(geometry, material);

      // Çarpı çizgileri
      const crossPoints = [
        new THREE.Vector3(-half, 0, 0), new THREE.Vector3(half, 0, 0),
        new THREE.Vector3(0, -half, 0), new THREE.Vector3(0, half, 0),
      ];
      const crossGeom = new THREE.BufferGeometry().setFromPoints(crossPoints);
      const crossMat = new THREE.LineBasicMaterial({ color: SNAP_COLOR, depthTest: false });
      const cross = new THREE.LineSegments(crossGeom, crossMat);

      const group = new THREE.Group();
      group.add(circle);
      group.add(cross);
      group.renderOrder = 9999;
      group.name = '__snapMarker';
      return group;
    }

    case 'extension': {
      // + Extension: artı işareti (AutoCAD extension marker)
      const points = [
        new THREE.Vector3(-half, 0, 0), new THREE.Vector3(half, 0, 0),
        new THREE.Vector3(0, -half, 0), new THREE.Vector3(0, half, 0),
      ];
      geometry = new THREE.BufferGeometry().setFromPoints(points);
      material = new THREE.LineBasicMaterial({ color: SNAP_COLOR, depthTest: false });
      marker = new THREE.LineSegments(geometry, material);
      break;
    }

    default: {
      // Varsayılan: Kare (endpoint gibi)
      const points = [
        new THREE.Vector3(-half, -half, 0),
        new THREE.Vector3(half, -half, 0),
        new THREE.Vector3(half, half, 0),
        new THREE.Vector3(-half, half, 0),
        new THREE.Vector3(-half, -half, 0),
      ];
      geometry = new THREE.BufferGeometry().setFromPoints(points);
      material = new THREE.LineBasicMaterial({ color: SNAP_COLOR, depthTest: false });
      marker = new THREE.Line(geometry, material);
    }
  }

  marker.renderOrder = 9999;
  marker.name = '__snapMarker';
  return marker;
}
